import { Button } from "@/components/ui/button";
import ContactEmailMenu from "@/components/ContactEmailMenu";
import {
  Sparkles, ArrowRight, HeartHandshake, Gauge, ListChecks, LayoutDashboard,
  MessageCircle, TrendingUp, Mail, Instagram, Briefcase, GraduationCap, ChevronDown,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const COACH_EMAIL = "decomplexe.formation@gmail.com";
const IG_URL = "https://www.instagram.com/decomplexe_formation/";

const atouts = [
  { icon: HeartHandshake, title: "Un accompagnement humain", text: "Un coach qui vous guide et vous aide à avancer." },
  { icon: Gauge, title: "Votre progression, à votre rythme", text: "Des objectifs adaptés à vos besoins et à votre niveau." },
  { icon: ListChecks, title: "Des missions concrètes", text: "Pour pratiquer, mémoriser et réutiliser ce que vous apprenez." },
  { icon: LayoutDashboard, title: "Un suivi personnalisé", text: "Retrouvez vos séances, vos ressources et vos missions dans votre espace personnel." },
];

const piliers = [
  { icon: MessageCircle, title: "Pratiquer", text: "Des séances guidées pour vous exprimer, essayer et apprendre sans craindre de faire des erreurs." },
  { icon: TrendingUp, title: "Progresser", text: "Des missions ciblées pour consolider vos acquis et transformer vos connaissances en réflexes." },
  { icon: HeartHandshake, title: "Être accompagné·e", text: "Un coach qui vous guide, vous aide à identifier vos difficultés et vous accompagne dans votre progression." },
];

export default function Landing() {
  const startCoachLogin = () => {
    const redirectUrl = window.location.origin + "/coach";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const scrollToOffres = () => document.getElementById("offres")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#D95D39] text-white flex items-center justify-center font-bold">D.</div>
          <span className="font-serif-accent text-xl tracking-tight">Décomplexé<span className="text-[#D95D39]">.e</span></span>
        </div>
        <Button
          data-testid="coach-login-google-button"
          onClick={startCoachLogin}
          variant="outline"
          className="dec-pill border-[#1F2421]/20 text-[#1F2421] bg-white hover:bg-[#F4EFEA]"
        >
          Connexion coach <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D95D39] font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> Coaching d'anglais humain
          </span>
          <h1 className="mt-4 font-serif-accent text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1F2421]">
            L'anglais, sans <em className="text-[#D95D39] not-italic underline decoration-wavy decoration-[#E5983B]/60 underline-offset-4">complexe</em>.
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-[#2A5B52] font-medium leading-snug max-w-lg">
            Libérez-vous de vos blocages. Retrouvez confiance. Osez parler anglais.
          </p>
          <div className="mt-6 space-y-4 text-base text-[#5C6561] leading-relaxed max-w-xl">
            <p>Vous comprenez quelques mots, vous saisissez parfois l'essentiel… mais dès qu'il faut parler, tout se complique ?</p>
            <p>Peur de vous tromper, manque de confiance, mauvais souvenirs des cours d'anglais : vous n'êtes pas seul·e. Et surtout, ce n'est pas une fatalité.</p>
            <p>Avec Décomplexé.e, vous avancez à votre rythme grâce à un coaching humain, des séances guidées et des missions adaptées à vos besoins.</p>
            <p>Parce que progresser en anglais, ce n'est pas viser la perfection. C'est oser faire le premier pas, puis le suivant.</p>
          </div>
          <div className="mt-8">
            <Button
              data-testid="hero-find-path-button"
              onClick={scrollToOffres}
              className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white h-12 px-7 text-base"
            >
              Trouvons votre parcours <ChevronDown className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-3xl bg-[#E5983B]/10 dec-grain" />
          <img
            className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-xl border border-[#E6E1DA]"
            src="https://static.prod-images.emergentagent.com/jobs/aa17b2d8-ee62-44e6-bbed-42927861342b/images/f6c5fe75941e68591039d1dd04bcfd112d62ab12b877054c289bafde4ad4aaac.jpeg"
            alt="Décomplexé.e — votre coach d'anglais"
          />
        </div>
      </section>

      {/* Atouts */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {atouts.map((a, i) => (
            <div key={i} className="dec-card p-6" data-testid={`atout-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center">
                <a.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold text-[#1F2421]">{a.title}</h3>
              <p className="mt-1.5 text-sm text-[#5C6561] leading-relaxed">{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Offres */}
      <section id="offres" className="max-w-6xl mx-auto px-6 py-16 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-serif-accent text-3xl sm:text-4xl text-[#1F2421]">Votre anglais. Vos objectifs. Votre parcours.</h2>
          <p className="mt-4 text-[#5C6561]">Vous n'avez pas les mêmes besoins selon votre situation. Choisissez le parcours qui vous correspond.</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {/* Offre 1 — English */}
          <div className="dec-card p-8 flex flex-col dec-side-accent" data-testid="offre-english">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D95D39] font-semibold">
              <Briefcase className="w-3.5 h-3.5" /> Pour les adultes
            </span>
            <h3 className="mt-3 font-serif-accent text-2xl text-[#1F2421]">Et si vous osiez enfin parler anglais ?</h3>
            <p className="mt-4 text-[#5C6561] leading-relaxed">
              Vous voulez voyager, évoluer professionnellement ou simplement vous sentir plus à l'aise en anglais ?
              Reprenez confiance, consolidez vos bases et apprenez à communiquer dans des situations concrètes.
              Avancez à votre rythme, sans pression et sans chercher à être parfait·e.
            </p>
            <p className="mt-5 font-medium text-[#2A5B52]">Votre objectif : oser utiliser l'anglais dans la vraie vie.</p>
            <div className="mt-6 mt-auto pt-6 flex flex-col sm:flex-row gap-3">
              <ContactEmailMenu subject="Renseignements – Décomplexé.e English" label="Parlons de votre projet" testid="offre-english-button" className="flex-1 justify-center" />
              <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="sm:w-auto">
                <Button data-testid="offre-english-instagram" variant="outline" className="dec-pill h-12 px-5 w-full bg-white border-[#1F2421]/15 text-[#1F2421] hover:bg-[#F4EFEA]">
                  <Instagram className="w-4 h-4 sm:mr-0 mr-2" /><span className="sm:hidden">Instagram</span>
                </Button>
              </a>
            </div>
          </div>

          {/* Offre 2 — Exams */}
          <div className="dec-card p-8 flex flex-col dec-side-accent" data-testid="offre-exams">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D95D39] font-semibold">
              <GraduationCap className="w-3.5 h-3.5" /> Pour les étudiants
            </span>
            <h3 className="mt-3 font-serif-accent text-2xl text-[#1F2421]">Réussir en anglais sans perdre confiance.</h3>
            <p className="mt-4 text-[#5C6561] leading-relaxed">
              Vous manquez de méthode, vous doutez de votre niveau ou les évaluations d'anglais vous stressent ?
              Consolidez vos bases, apprenez à travailler efficacement et préparez vos examens avec un accompagnement adapté à vos besoins.
            </p>
            <p className="mt-5 font-medium text-[#2A5B52]">Votre objectif : progresser avec méthode et aborder vos examens plus sereinement.</p>
            <div className="mt-6 mt-auto pt-6 flex flex-col sm:flex-row gap-3">
              <ContactEmailMenu subject="Renseignements – Décomplexé.e Exams" label="Parlons de vos objectifs" testid="offre-exams-button" className="flex-1 justify-center" />
              <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="sm:w-auto">
                <Button data-testid="offre-exams-instagram" variant="outline" className="dec-pill h-12 px-5 w-full bg-white border-[#1F2421]/15 text-[#1F2421] hover:bg-[#F4EFEA]">
                  <Instagram className="w-4 h-4 sm:mr-0 mr-2" /><span className="sm:hidden">Instagram</span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Méthode */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-serif-accent text-3xl sm:text-4xl text-[#1F2421]">Vous n'avez pas besoin d'être parfait·e pour progresser.</h2>
          <p className="mt-4 text-[#5C6561] leading-relaxed">
            La confiance ne vient pas avant de se lancer. Elle se construit en pratiquant, en comprenant ses erreurs et en avançant étape après étape.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {piliers.map((p, i) => (
            <div key={i} className="dec-card p-7 text-center" data-testid={`pilier-${i}`}>
              <div className="mx-auto w-12 h-12 rounded-full bg-[#F1F6F2] text-[#2A5B52] flex items-center justify-center">
                <p.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold uppercase tracking-wide text-sm text-[#1F2421]">{p.title}</h3>
              <p className="mt-2 text-sm text-[#5C6561] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Appel à l'action final */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="dec-card p-10 md:p-14 text-center bg-[#1F2421] text-white border-none">
          <h2 className="font-serif-accent text-3xl sm:text-4xl">Et si vous arrêtiez de vous dire : « Je suis nul·le en anglais » ?</h2>
          <p className="mt-4 text-white/75 max-w-2xl mx-auto leading-relaxed">
            Vous n'avez pas besoin d'attendre d'avoir confiance pour commencer. Vous pouvez la construire en avançant.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <ContactEmailMenu subject="Mon projet avec Décomplexé.e" label="Me contacter par e-mail" testid="final-email-button" />
            <a href={IG_URL} target="_blank" rel="noopener noreferrer">
              <Button data-testid="final-instagram-button" variant="outline" className="dec-pill bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-6 w-full sm:w-auto">
                <Instagram className="w-4 h-4 mr-2" /> Me contacter sur Instagram
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E6E1DA]">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D95D39] text-white flex items-center justify-center font-bold text-sm">D.</div>
            <span className="text-sm text-[#5C6561]">Décomplexé.e — Formation &amp; transformation.</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <ContactEmailMenu subject="Mon projet avec Décomplexé.e" label="Contact par e-mail" testid="footer-email-menu" variant="subtle" className="!h-9 !px-4 !w-auto text-sm" />
            <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="text-[#1F2421] hover:text-[#D95D39] inline-flex items-center gap-1.5" data-testid="footer-instagram-link">
              <Instagram className="w-4 h-4" /> Instagram
            </a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-8 -mt-2">
          <p className="text-xs text-[#8A918C]">
            Vous êtes apprenant·e ? Utilisez simplement le lien privé envoyé par votre coach — aucun mot de passe requis.
          </p>
        </div>
      </footer>
    </div>
  );
}
